// Persona-driven onboarding helpers. Pure functions, no side effects.

export type Persona =
  | "content-creator"
  | "medical"
  | "lawyer"
  | "coach-tutor"
  | "hearing-loss"
  | "it-specialist"
  | "student"
  | "business"
  | "other";

// Primary intent — the job the user wants done FIRST. Drives the aha-demo
// step, the intent-keyed paywall (headline/CTA/spec bullets), and the
// post-purchase deep link into that action.
export type OnboardingIntent =
  | "import"
  | "record"
  | "dictate"
  | "study"
  | "translate";

export const ALL_INTENTS: ReadonlyArray<OnboardingIntent> = [
  "import",
  "record",
  "dictate",
  "study",
  "translate",
];

// Persona → intent display order. Highest-affinity job first; every intent
// stays visible so nothing disappears.
export const getIntentOrder = (
  persona: Persona | null,
): ReadonlyArray<OnboardingIntent> => {
  const priorityByPersona: Partial<Record<Persona, OnboardingIntent[]>> = {
    lawyer: ["import", "dictate", "record", "translate", "study"],
    medical: ["dictate", "record", "import", "translate", "study"],
    business: ["record", "import", "dictate", "translate", "study"],
    "content-creator": ["import", "translate", "record", "dictate", "study"],
    student: ["study", "record", "import", "translate", "dictate"],
    "coach-tutor": ["record", "study", "import", "dictate", "translate"],
    "it-specialist": ["dictate", "record", "import", "translate", "study"],
    "hearing-loss": ["record", "translate", "import", "study", "dictate"],
  };
  return (persona && priorityByPersona[persona]) || ALL_INTENTS;
};

// Study and translation begin with a file on desktop; show every capability once.
export const getDesktopWorkflowOrder = (
  persona: Persona | null,
): readonly ("upload" | "record" | "dictate")[] => [
  ...new Set(
    getIntentOrder(persona).map((intent) =>
      intent === "record" || intent === "dictate" ? intent : "upload",
    ),
  ),
];

// Personas on Android that benefit from the dictation mini-flow (step 7) and the
// dictation quick-setup step (step 14). Non-matched personas don't see those
// screens — dictation stays available later via the home bubble + Settings.
export const DICTATION_MATCH_PERSONAS: ReadonlySet<Persona> = new Set<Persona>([
  "content-creator",
  "medical",
  "lawyer",
  "it-specialist",
  "business",
]);

export const isDictationMatchPersona = (
  persona: Persona | null | undefined,
): boolean => (persona ? DICTATION_MATCH_PERSONAS.has(persona) : false);

// Hearing-loss users buy ACCESS, not time saved — every hours-saved framing
// (result working-days line and paywall savings eyebrow) should check this
// instead of hardcoding the persona.
export const personaUsesTimeFraming = (persona: Persona | null): boolean =>
  persona !== "hearing-loss";

// Personas whose primary path is uploading existing audio/video.
const UPLOAD_FIRST_PERSONAS: ReadonlySet<Persona> = new Set<Persona>([
  "lawyer",
  "content-creator",
  "student",
  "business",
  "it-specialist",
  "other",
]);

export const isUploadFirstPersona = (
  persona: Persona | null | undefined,
): boolean => (persona ? UPLOAD_FIRST_PERSONAS.has(persona) : true);

// Persona → key suffix used by `t()` to resolve persona-flavored copy
// (falls back to "other" when persona is null / unknown).
export const getPersonaSuffix = (persona: Persona | null): string =>
  persona || "other";

// Ordered by expected user-base share — Business and Student at the top
// because they're the two largest segments.
export const ALL_PERSONAS: ReadonlyArray<Persona> = [
  "business",
  "student",
  "content-creator",
  "medical",
  "lawyer",
  "coach-tutor",
  "it-specialist",
  "hearing-loss",
  "other",
];

// Persona → i18n key suffix to support `t(\`onboarding.<ns>.headline_${suffix}\`)`.
// Always returns a string; falls back to "other" for null/unknown personas.
const personaSuffix = (persona: Persona | null): string => persona || "other";

export const getPersonaPaywallHeadlineKey = (persona: Persona | null): string =>
  `onboarding.paywall.headline_${personaSuffix(persona)}`;

export const getPersonaResultSubheadKey = (persona: Persona | null): string =>
  `onboarding.result.subhead_${personaSuffix(persona)}`;

export const getPersonaDictationCopyKey = (persona: Persona | null): string =>
  `onboarding.dictation.copy_${personaSuffix(persona)}`;

// Picks the right caption for the time-flow chart (steps 4 & 5).
// Ordering matters: stronger / more specific use cases win over generic ones.
export const getUseCaseWorkflowLabel = (useCases: string[]): string => {
  const set = new Set(useCases);

  if (set.has("legal") || set.has("medical")) {
    return "onboarding.timeLost.workflow_documentation";
  }
  if (set.has("meetings") || set.has("interviews")) {
    return "onboarding.timeLost.workflow_meetings";
  }
  if (set.has("lectures")) {
    return "onboarding.timeLost.workflow_lectures";
  }
  if (set.has("voiceNotes") || set.has("depositions")) {
    return "onboarding.timeLost.workflow_typing";
  }
  if (set.has("videos") || set.has("podcasts")) {
    return "onboarding.timeLost.workflow_content";
  }
  if (useCases.length > 1) {
    return "onboarding.timeLost.workflow_mixed";
  }
  return "onboarding.timeLost.workflow_default";
};

// Picks the right "did you know" stat in step 4. Decision tree from the plan:
// meetings > lectures > voice-notes/dictation-leaning > videos/podcasts >
// interviews/depositions > default.
export const getUseCaseStatKey = (useCases: string[]): string => {
  const set = new Set(useCases);
  if (set.has("meetings")) return "onboarding.timeLost.stat_meetings";
  if (set.has("lectures")) return "onboarding.timeLost.stat_lectures";
  if (set.has("voiceNotes")) return "onboarding.timeLost.stat_voiceNotes";
  if (set.has("videos") || set.has("podcasts")) {
    return "onboarding.timeLost.stat_content";
  }
  if (set.has("interviews") || set.has("depositions")) {
    return "onboarding.timeLost.stat_interviews";
  }
  if (set.has("legal") || set.has("medical")) {
    return "onboarding.timeLost.stat_documentation";
  }
  return "onboarding.timeLost.stat_default";
};

// Marketing-grade estimator. Not a real measurement — just a sensible number
// anchored to the persona-flavored headline copy. Clamped to [6, 30] hours/mo.
// Persona is the only input since onboarding stopped collecting use cases.
// The base carries what the removed use-case bonus used to contribute: with a
// base of 4 every persona landed on the `Math.max(6, …)` floor, so the
// multiplier below was silently doing nothing and every persona was quoted the
// same 6 hours.
export const estimateHoursSavedPerMonth = (persona: Persona | null): number => {
  const base = 8;
  const personaMultiplier: Record<Persona, number> = {
    lawyer: 1.6,
    business: 1.5,
    medical: 1.4,
    "coach-tutor": 1.3,
    student: 1.2,
    "content-creator": 1.2,
    "it-specialist": 1.1,
    "hearing-loss": 1.0,
    other: 1.0,
  };
  const mult = personaMultiplier[persona || "other"];
  const hours = Math.round(base * mult);
  return Math.max(6, Math.min(30, hours));
};
